import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "armada_access_token";
const REFRESH_KEY = "armada_refresh_token";
const MFA_PENDING_KEY = "armada_mfa_pending_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = authed
  const [mfaPending, setMfaPending] = useState(null); // { token, user }

  const loadMe = useCallback(async () => {
    // CRITICAL: Skip /me check when returning from OAuth callback — let AuthCallback handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const storeTokens = (access, refresh) => {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  };

  const handleAuthResponse = (data) => {
    if (data.mfa_required) {
      localStorage.setItem(MFA_PENDING_KEY, data.access_token);
      setMfaPending({ token: data.access_token, user: data.user });
      return { mfaRequired: true, user: data.user };
    }
    storeTokens(data.access_token, data.refresh_token);
    setMfaPending(null);
    localStorage.removeItem(MFA_PENDING_KEY);
    setUser(data.user);
    return { mfaRequired: false, user: data.user };
  };

  const loginWithPassword = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return handleAuthResponse(data);
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const verifyMfa = async (code) => {
    const pending = localStorage.getItem(MFA_PENDING_KEY);
    if (!pending) throw new Error("No MFA challenge pending");
    try {
      const { data } = await api.post(
        "/auth/mfa/verify-login",
        { code },
        { headers: { Authorization: `Bearer ${pending}` } }
      );
      return handleAuthResponse(data);
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const requestMagicLink = async (email) => {
    try {
      const { data } = await api.post("/auth/magic-link", { email });
      return data;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const verifyMagicLink = async (token) => {
    try {
      const { data } = await api.post("/auth/magic-link/verify", { token });
      return handleAuthResponse(data);
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      return data;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const { data } = await api.post("/auth/password/reset", { token, new_password: newPassword });
      return data;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(MFA_PENDING_KEY);
    setUser(false);
    setMfaPending(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  const exchangeGoogleSession = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      return handleAuthResponse(data);
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const linkGoogle = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/link/google", { session_id: sessionId });
      await refreshUser();
      return data;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const unlinkProvider = async (provider) => {
    try {
      const { data } = await api.post(`/auth/unlink/${provider}`);
      await refreshUser();
      return data;
    } catch (e) {
      throw new Error(formatApiError(e));
    }
  };

  const fetchLinked = async () => {
    const { data } = await api.get("/auth/linked");
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        mfaPending,
        loginWithPassword,
        verifyMfa,
        requestMagicLink,
        verifyMagicLink,
        requestPasswordReset,
        resetPassword,
        logout,
        refreshUser,
        exchangeGoogleSession,
        linkGoogle,
        unlinkProvider,
        fetchLinked,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
